/**
 * CRM Mutation Hooks
 * Mutations for CRM operations (Monday.com integration)
 */

import { useMutation } from "@tanstack/react-query";
import { useAuth } from "../../useAuth";
import { apiGet } from "../../../lib/api-utils";
import { ENDPOINTS, RETRY_CONFIG } from "../config";
import type { CRMSearchResult, CRMSearchParams } from "../types";
import { buildQueryParams } from "../utils";

/**
 * Search CRM (Monday.com)
 * Searches for items across Monday.com boards
 */
export const useCRMSearch = () => {
  const { getAuthToken } = useAuth();

  return useMutation({
    mutationFn: async ({
      query,
      quickFind,
      boardId,
    }: CRMSearchParams): Promise<CRMSearchResult[]> => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const queryParams = buildQueryParams({
        search: query,
        quickFind: quickFind ? "true" : undefined,
        boardId: boardId?.toString(),
      });

      const response = await apiGet(
        ENDPOINTS.crm.search,
        token,
        queryParams
      );

      const allResults: CRMSearchResult[] = [];

      Object.entries(response).forEach(([boardName, items]) => {
        if (Array.isArray(items)) {
          items.forEach((item: any) => {
            allResults.push({
              objectID:
                item.id || item.objectID || `${boardName}-${Math.random()}`,
              title: item.title || item.name || item.text || boardName,
              name: item.name || item.title,
              description: item.description || item.text,
              type: "monday_item",
              url: item.url || `#${item.id}`,
              iconType: "monday",
              subtitle: boardName,
              tags: item.tags || [],
            });
          });
        }
      });

      return allResults;
    },
    retry: RETRY_CONFIG.standard,
  });
};
